import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import BillingStatusCard from "../components/billing/BillingStatusCard";
import BillingInfo from "../components/billing/BillingInfo";
import ReferralCard from "../components/ReferralCard";
import { apiErrorMessage } from "../lib/errors";

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 10;

export default function Billing() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/status");
      setStatus(data);
    } finally {
      setLoading(false);
    }
  }, []);

  const pollSession = useCallback(
    async (sessionId, attempt = 0) => {
      try {
        const { data } = await api.get(`/billing/checkout/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Payment succeeded — Pro week added");
          await loadStatus();
          setSearchParams({});
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout session expired");
          setSearchParams({});
          return;
        }
        if (attempt >= MAX_POLL_ATTEMPTS) {
          toast.message("Still processing — refresh in a moment");
          return;
        }
        setTimeout(() => pollSession(sessionId, attempt + 1), POLL_INTERVAL_MS);
      } catch (err) {
        toast.error(apiErrorMessage(err, "Could not verify payment"));
      }
    },
    [loadStatus, setSearchParams]
  );

  useEffect(() => {
    loadStatus();
    const sid = searchParams.get("session_id");
    if (sid) pollSession(sid);
  }, [loadStatus, pollSession, searchParams]);

  const startCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: "weekly",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(apiErrorMessage(err, "Checkout failed"));
      setCheckoutLoading(false);
    }
  };

  const cancelSub = async () => {
    if (!window.confirm("Cancel auto-renewal? You'll keep Pro access until the end of this period.")) return;
    try {
      await api.post("/billing/cancel");
      toast.success("Auto-renewal canceled — access continues until period ends");
      await loadStatus();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Cancel failed"));
    }
  };

  const refreshFromStripe = async () => {
    try {
      await api.post("/billing/refresh");
      await loadStatus();
      toast.success("Synced with Stripe");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Refresh failed"));
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/billing/portal");
      window.location.href = data.url;
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not open billing portal"));
    }
  };

  return (
    <div data-testid="billing-page">
      <div className="border-b border-zinc-800 pb-10 mb-10">
        <div className="overline mb-3">BILLING · SUBSCRIPTION</div>
        <h1 className="font-display text-4xl lg:text-5xl">Pro access.</h1>
        <p className="mt-4 text-zinc-400 max-w-2xl">
          3 days free, then $9.99/week for unlimited research, arbitrage scanner and the
          morning email card. Cancel in one click.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5 space-y-4">
          {loading && !status ? (
            <div
              className="border border-zinc-800 p-6 flex items-center gap-2 text-zinc-400 mono text-sm"
              data-testid="billing-status-card"
            >
              <Loader2 className="animate-spin text-[#00D4FF]" size={16} /> Loading status…
            </div>
          ) : (
            <BillingStatusCard
              status={status}
              checkoutLoading={checkoutLoading}
              onCheckout={startCheckout}
              onCancel={cancelSub}
              onRefresh={refreshFromStripe}
            />
          )}
          <button
            onClick={openPortal}
            data-testid="billing-portal-button"
            className="w-full px-4 py-3 border border-zinc-700 text-zinc-300 hover:border-[#00D4FF] hover:text-[#00D4FF] font-mono uppercase tracking-widest text-xs"
          >
            Manage card &amp; invoices (Stripe Portal)
          </button>
          <ReferralCard />
        </div>
        <BillingInfo />
      </div>
    </div>
  );
}